import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, MapPin, X, Loader2 } from 'lucide-react';
import BusinessCard from '../components/BusinessCard';
import { getBusinesses, searchBusinesses } from '../services/firestore';
import { BusinessListItem } from '../types';

const Explore: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState<BusinessListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'barbershops', label: 'Barbershops' },
    { id: 'hair salons', label: 'Hair Salons' },
    { id: 'braiding', label: 'Braiding' },
    { id: 'locs & twists', label: 'Locs & Twists' },
  ];

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      let result: BusinessListItem[];
      
      if (searchQuery) {
        result = await searchBusinesses(searchQuery);
      } else {
        result = await getBusinesses();
      }
      
      // Filter by category if not 'all'
      if (selectedCategory !== 'all') {
        result = result.filter(b => 
          b.name.toLowerCase().includes(selectedCategory.toLowerCase())
        );
      }
      
      setBusinesses(result);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(prev => {
      if (searchQuery) {
        prev.set('search', searchQuery);
      } else {
        prev.delete('search');
      }
      return prev;
    });
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSearchParams(prev => {
      if (categoryId !== 'all') {
        prev.set('category', categoryId);
      } else {
        prev.delete('category');
      }
      return prev;
    });
  };

  return (
    <div className="min-h-screen bg-prim-background">
      {/* Search Header */}
      <div className="sticky top-16 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for services..."
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchParams(prev => {
                        prev.delete('search');
                        return prev;
                      });
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </form>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-xl border flex items-center space-x-2 transition-all ${
                showFilters 
                  ? 'bg-primary text-white border-primary' 
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              <SlidersHorizontal size={18} />
              <span className="font-medium">Filters</span>
            </button>
          </div>

          {/* Category Pills */}
          <div className="flex items-center space-x-2 mt-4 overflow-x-auto no-scrollbar pb-1">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white border-b border-gray-100"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Distance Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance
                </label>
                <select className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:border-primary">
                  <option value="5">Within 5 km</option>
                  <option value="10">Within 10 km</option>
                  <option value="25">Within 25 km</option>
                  <option value="50">Within 50 km</option>
                </select>
              </div>

              {/* Rating Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Rating
                </label>
                <select className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:border-primary">
                  <option value="0">Any Rating</option>
                  <option value="4">4+ Stars</option>
                  <option value="4.5">4.5+ Stars</option>
                </select>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Range
                </label>
                <select className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:border-primary">
                  <option value="all">All Prices</option>
                  <option value="budget">Budget Friendly</option>
                  <option value="mid">Mid Range</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              {/* Availability */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability
                </label>
                <select className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:border-primary">
                  <option value="all">Any Time</option>
                  <option value="today">Available Today</option>
                  <option value="week">This Week</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-display font-bold text-prim-text">
              {searchQuery ? `Results for "${searchQuery}"` : 'Explore'}
            </h1>
            {!loading && (
              <p className="text-gray-500 mt-1">
                {businesses.length} {businesses.length === 1 ? 'business' : 'businesses'} found
              </p>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <p className="text-gray-500">Loading businesses...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && businesses.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <MapPin className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-prim-text mb-2">No businesses found</h3>
            <p className="text-gray-500 mb-6">
              Try adjusting your search or filters to find what you're looking for.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSearchParams({});
              }}
              className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Business Grid */}
        {!loading && businesses.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {businesses.map((business, index) => (
              <BusinessCard 
                key={business.businessId} 
                business={business} 
                index={index}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Explore;

